"use client";

import { useRef } from "react";
import type { Pollable } from "@theta/api-client";

/**
 * THE `?ifChanged=` BOOKKEEPING A POLLED READ NEEDS, IN ONE PLACE
 *
 * A conditional read (§1.10/D12) is two lines of protocol and four lines of
 * memory: hold the last `state` and the last document, send the state, and put
 * the SAME document reference back when the server says nothing moved. That
 * last part is not an optimisation — returning a fresh array with equal
 * contents makes react-query publish a new reference every tick, and every
 * consumer of the query re-renders once a second for a thread nobody is
 * writing in.
 *
 * The two fastest polls in the app (`useComments` and `useFeedItem`) each need
 * exactly this, which is why it is a helper rather than two copies.
 * `useNotifications`, `useConversations` and
 * `useHomeFeed` hand-roll their own because they are infinite queries and
 * remember one entry PER PAGE; folding those in would mean generalising this
 * over a page key for no reader's benefit.
 *
 * KEYED BY THE SUBJECT, so navigating from one post to another does not offer
 * the new post the old one's token. A miss is harmless — an unrecognised token
 * simply produces the full document (§1.10) — but the stale DOCUMENT that
 * comes back with it on an `unchanged` tick would not be.
 */
export interface Remembered<T> {
  state: string;
  data: T;
}

export interface IfChangedMemory<T> {
  /** The token to send, or `undefined` on the first read of this subject. */
  token: () => string | undefined;
  /**
   * Resolve one answer: the fresh document, or the remembered one on an
   * `unchanged` tick. Throws only in the impossible case — the server answered
   * `unchanged` for a token this hook never sent.
   */
  resolve: (result: Pollable<T>, fallback: T) => T;
}

export function useIfChanged<T>(subject: number): IfChangedMemory<T> {
  const seen = useRef<{ subject: number; remembered: Remembered<T> } | null>(null);
  const current = () =>
    seen.current?.subject === subject ? seen.current.remembered : null;

  return {
    token: () => current()?.state,
    resolve: (result, fallback) => {
      if (result.unchanged) return current()?.data ?? fallback;
      seen.current = {
        subject,
        remembered: { state: result.state ?? "", data: result.data },
      };
      return result.data;
    },
  };
}
